import type { FC } from "react"
import { useCallback, useLayoutEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Popover,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import type { PopoverActions } from "@mui/material"
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded"
import CloseRoundedIcon from "@mui/icons-material/CloseRounded"

import type { EntityType } from "types/common.types"
import type { IDateRange } from "types/date.types"
import type { DateRefOption } from "helpers/hooks/useSelectOptions"
import DateReferenceList from "./DateReferenceList"
import DateRangeEditor from "./DateRangeEditor"
import { PANEL_LINE } from "./DateRangeEditor/constants"
import { formatRangeLabel, getPanelPlacement } from "./helpers"
import { usePickerLocale } from "./hooks"

/** Just under the trigger's tooltip (1298); the calendar opens inline, so nothing floats above. */
const DATE_RANGE_POPOVER_Z_INDEX = 1299
/** Fits the longest reference with its forecast tag on one line, in English and Spanish. */
const REFERENCES_WIDTH = 280

interface DateRangePopoverProps {
  id: string
  "aria-label": string
  open: boolean
  anchorEl: HTMLElement | null
  onClose: () => void
  /** The trigger's edge the list lines up with; the editor always opens to the list's left. */
  referencesSide?: "left" | "right"
  options: DateRefOption[]
  /** The applied reference, "custom" included. */
  range: string
  /** The applied dates, which the custom period starts from. */
  value: IDateRange
  entity: EntityType
  maxRangeMonths?: number
  onSelectReference: (reference: string) => void
  onApply: (range: IDateRange) => void
}

/**
 * The date range picker's shell: the reference list in a popover, joined by the custom
 * period editor once it is picked, or both as two steps of a full-screen dialog on small
 * screens.
 */
const DateRangePopover: FC<DateRangePopoverProps> = ({
  id,
  "aria-label": ariaLabel,
  open,
  anchorEl,
  onClose,
  referencesSide = "right",
  options,
  range,
  value,
  entity,
  maxRangeMonths,
  onSelectReference,
  onApply,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const locale = usePickerLocale()
  const isMediumOrSmallDevice = useMediaQuery(theme.breakpoints.down("md"))
  // "Custom period" only unlocks the fields; the range changes on Apply.
  const [isCustom, setIsCustom] = useState(range === "custom")
  const [isMobileEditorOpen, setIsMobileEditorOpen] = useState(false)
  const popoverActions = useRef<PopoverActions>(null)

  // Placed again before paint once the editor widens it, so the list stays under the
  // trigger and the editor grows away from it.
  useLayoutEffect(() => {
    popoverActions.current?.updatePosition()
  }, [isCustom])

  const handleSelect = useCallback(
    (reference: string) => {
      if (reference === "custom") {
        setIsCustom(true)
        setIsMobileEditorOpen(true)
        return
      }
      onSelectReference(reference)
      onClose()
    },
    [onClose, onSelectReference],
  )

  const handleApply = useCallback(
    (picked: IDateRange) => {
      onApply(picked)
      onClose()
    },
    [onApply, onClose],
  )

  const handleBack = useCallback(() => {
    setIsMobileEditorOpen(false)
    setIsCustom(range === "custom")
  }, [range])

  const references = (
    <DateReferenceList
      options={options}
      selectedValue={isCustom ? "custom" : range}
      onSelect={handleSelect}
      appliedRange={formatRangeLabel(value.fromDate, value.toDate, locale)}
    />
  )
  const editor = (
    <DateRangeEditor
      value={value}
      isRangeChange={range !== "custom"}
      entity={entity}
      maxRangeMonths={maxRangeMonths}
      onApply={handleApply}
      onCancel={isMediumOrSmallDevice ? handleBack : onClose}
    />
  )

  if (isMediumOrSmallDevice) {
    return (
      <Dialog fullScreen open={open} onClose={onClose} id={id} aria-label={ariaLabel}>
        <DialogTitle
          component={Stack}
          direction="row"
          alignItems="center"
          gap={1}
          sx={{
            borderBottom: 1,
            borderColor: PANEL_LINE,
            // Same title as the CSV download drawer.
            typography: "subtitle1",
            fontWeight: 600,
          }}
        >
          {isMobileEditorOpen && (
            <IconButton
              edge="start"
              aria-label={t("dates.BACK_TO_PERIODS")}
              onClick={handleBack}
            >
              <ArrowBackRoundedIcon />
            </IconButton>
          )}
          <Box component="span" sx={{ flex: 1 }}>
            {t(isMobileEditorOpen ? "dates.CUSTOM" : "dates.PERIOD")}
          </Box>
          {!isMobileEditorOpen && (
            <IconButton edge="end" aria-label={t("generic.CLOSE")} onClick={onClose}>
              <CloseRoundedIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent
          // MUI drops the top padding after a DialogTitle, which clips the field labels.
          sx={{ "&&": { p: isMobileEditorOpen ? 2 : 0 } }}
        >
          {isMobileEditorOpen ? editor : references}
        </DialogContent>
      </Dialog>
    )
  }

  const placement = getPanelPlacement(open ? anchorEl : null)

  return (
    <Popover
      action={popoverActions}
      id={id}
      aria-label={ariaLabel}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      // The paper hangs from the list's right edge, so the editor grows leftwards. On the
      // left, that edge is a list's width past the trigger's left.
      anchorOrigin={{
        vertical: placement.above ? "top" : "bottom",
        horizontal: referencesSide === "right" ? "right" : REFERENCES_WIDTH,
      }}
      transformOrigin={{
        vertical: placement.above ? "bottom" : "top",
        horizontal: "right",
      }}
      sx={{
        zIndex: DATE_RANGE_POPOVER_Z_INDEX,
        // A panel this large reads as a dialog, so it takes the dialog surface.
        "& .MuiPopover-paper": theme.mixins.dialogSurface,
      }}
    >
      <Box
        sx={{
          display: "flex",
          // Its own width, so the paper is measured whole and not squeezed against the edge.
          width: "max-content",
          // The same height with or without the editor.
          height: placement.height,
        }}
      >
        {isCustom && (
          <Box
            sx={{
              // The design's padding, a little under the dialogs' 24px to save height.
              p: 2.5,
              overflowY: "auto",
              // The same line as the popover's border, on the editor so the list does not move.
              borderRight:
                theme.mixins.dialogSurface.border ?? `1px solid ${theme.palette.divider}`,
            }}
          >
            {editor}
          </Box>
        )}
        <Box sx={{ width: REFERENCES_WIDTH, flexShrink: 0 }}>{references}</Box>
      </Box>
    </Popover>
  )
}

export default DateRangePopover
